/**
 * Editing a table: its shape, how its cells are set, and which are merged.
 *
 * Pure. Every operation takes a table and returns a new one, and none of them
 * can leave the table ragged: a row always has exactly as many cells as the
 * header, whatever was inserted, removed or merged. The inspector is a thin
 * layer over these, so the rules live in one place and are tested without a
 * browser.
 *
 * Three families of operation, kept apart on purpose.
 *
 * **Structure.** Rows and columns, inserted, removed and moved. A table always
 * keeps at least one column, because a table with none cannot be edited back
 * into having any. A merged cell follows the rows and columns it stands on: a
 * row inserted inside it makes it taller, a row removed from it makes it
 * shorter, and a row tied into one is not moved away from the rest of it.
 *
 * **Setting.** How a cell is aligned, coloured and weighted. A setting is
 * applied to a *target*: one cell, a row, a column, a block of cells or the
 * whole table, so colouring a total row is one operation rather than one per
 * cell. What a setting never does is touch a cell's value: nothing here can
 * put a wrong figure in a signed document.
 *
 * **Merging.** A block of cells made into one, and made back into its cells.
 * Which cell is drawn where lives in `spans.ts`; here is only what a merge
 * does to the cells themselves.
 */

import { isAccent } from '@/domain/layout'
import { emptyCell, type CellAlign, type TableCell, type TableSection } from '@/domain/section'
import { widthsAfterInsert, widthsAfterMove, widthsAfterRemove } from '@/sections/table/widths'
import {
  areaBetween,
  areaOfDrawn,
  cellAtLine,
  columnCount,
  columnIsTied,
  drawnIn,
  inside,
  isMerged,
  lineIsTied,
  lineOf,
  mergesAcrossColumn,
  mergesAcrossLine,
  planOf,
  rowOf,
  settleArea,
  startsAt,
  type CellArea,
  type CellPoint,
  type DrawnCell,
  type RowRef,
} from '@/sections/table/spans'

export { columnCount } from '@/sections/table/spans'
export type { CellPoint, RowRef } from '@/sections/table/spans'

/**
 * What an operation applies to.
 *
 * The scopes a spreadsheet offers. A range is a block of cells, chosen by
 * dragging across them or with Shift and a click; it is what a merge is made
 * from, and it is set like any other target.
 */
export type CellTarget =
  | { readonly scope: 'cell'; readonly row: RowRef; readonly column: number }
  | { readonly scope: 'row'; readonly row: RowRef }
  | { readonly scope: 'column'; readonly column: number }
  | { readonly scope: 'range'; readonly from: CellPoint; readonly to: CellPoint }
  | { readonly scope: 'table' }

export const CELL_ALIGNMENTS: readonly CellAlign[] = ['left', 'center', 'right']

/**
 * Tints for a cell's background.
 *
 * Pale on purpose: a fill sits under printed figures, and anything stronger
 * than this either hides them or turns to a grey slab on a photocopy. A fill
 * says "this row is different"; it is not there to be looked at.
 */
export const CELL_FILLS = {
  grey: '#eef1f4',
  blue: '#e6eef8',
  green: '#e7f2ec',
  amber: '#fbf1dc',
  rose: '#f9e6e6',
  teal: '#e3f1f0',
} as const

/**
 * Inks for a cell's text.
 *
 * Dark enough to read at nine points, each of them. A cell in a colour that
 * looks fine on screen and prints as a faint smudge is the failure.
 */
export const CELL_INKS = {
  ink: '#16202c',
  grey: '#6b7785',
  navy: '#1c3f6e',
  green: '#1e6b45',
  red: '#b3261e',
} as const

export type CellFillPreset = keyof typeof CELL_FILLS
export type CellInkPreset = keyof typeof CELL_INKS

export const CELL_FILL_IDS = Object.keys(CELL_FILLS) as readonly CellFillPreset[]
export const CELL_INK_IDS = Object.keys(CELL_INKS) as readonly CellInkPreset[]

/** A stored alignment, if it is one this version offers. */
export function readCellAlign(value: unknown): CellAlign | undefined {
  return typeof value === 'string' && (CELL_ALIGNMENTS as readonly string[]).includes(value)
    ? (value as CellAlign)
    : undefined
}

/**
 * A stored colour, if it is safe to write into a stylesheet.
 *
 * The same six-digit rule as the document's accent, for the same reason: the
 * value reaches an inline style, a report is shared, and a permissive check
 * would let one person's cell rewrite another's page. Anything else is
 * dropped rather than repaired, because a colour that is almost right looks
 * deliberate.
 */
export function readCellColour(value: unknown): string | undefined {
  return typeof value === 'string' && isAccent(value) ? value.toLowerCase() : undefined
}

/**
 * The rule a cell draws along its top or bottom edge, in the table's colour.
 *
 * Set inline on the cell, which is what lets it show where the page's own rules
 * would hide it (the foot of the last row) and travel into an export as it is.
 * Heavier than the 1px rule between rows, so where two cells meet in a table
 * with collapsed borders the stronger one is the one drawn.
 */
export const CELL_RULE = '1.5px solid var(--table-head, var(--page-accent))'

/** How a cell is set, apart from what it is. */
export interface CellStyle {
  align?: CellAlign
  fill?: string
  ink?: string
  strong?: boolean
  ruleAbove?: boolean
  ruleBelow?: boolean
}

/**
 * A change to how cells are set.
 *
 * `undefined` leaves a property alone and `null` clears it, so one change can
 * say "make these red" or "take the colour off" without a second function for
 * every property.
 */
export type CellStyleChange = { readonly [K in keyof CellStyle]?: CellStyle[K] | null }

/** A cell mid-edit, where a property may be present and set to nothing. */
type LooseCell = { readonly [K in keyof TableCell]?: TableCell[K] | undefined } & {
  readonly value: string
}

/**
 * A cell with only the properties it actually has.
 *
 * Every operation returns cells through this, so a cleared setting is gone
 * from the stored document rather than present as `false` or `undefined`.
 * The document is compared field by field when it is saved, and a key that is
 * there in one version and absent in the next reads as a change. A span of
 * one is no span, and is dropped the same way.
 */
function clean(cell: LooseCell): TableCell {
  const next: TableCell = { value: cell.value }
  if (cell.numeric) next.numeric = true
  if (cell.strong) next.strong = true
  if (cell.align) next.align = cell.align
  if (cell.fill) next.fill = cell.fill
  if (cell.ink) next.ink = cell.ink
  if (cell.ruleAbove) next.ruleAbove = true
  if (cell.ruleBelow) next.ruleBelow = true
  if (cell.colspan !== undefined && cell.colspan > 1) next.colspan = cell.colspan
  if (cell.rowspan !== undefined && cell.rowspan > 1) next.rowspan = cell.rowspan
  return next
}

export function restyle(cell: TableCell, change: CellStyleChange): TableCell {
  return clean({
    ...cell,
    align: change.align === undefined ? cell.align : (change.align ?? undefined),
    fill: change.fill === undefined ? cell.fill : (change.fill ?? undefined),
    ink: change.ink === undefined ? cell.ink : (change.ink ?? undefined),
    strong: change.strong === undefined ? cell.strong : (change.strong ?? undefined),
    ruleAbove: change.ruleAbove === undefined ? cell.ruleAbove : (change.ruleAbove ?? undefined),
    ruleBelow: change.ruleBelow === undefined ? cell.ruleBelow : (change.ruleBelow ?? undefined),
  })
}

/**
 * Whether a target takes in the cell at this position.
 *
 * A range is taken as it stands, corner to corner. Growing it over the merged
 * cells it cuts is `settleTarget`'s job, done once when it is chosen, so this
 * can stay a plain test that needs no table.
 */
export function covers(target: CellTarget, row: RowRef, column: number): boolean {
  switch (target.scope) {
    case 'cell':
      return target.row === row && target.column === column
    case 'row':
      return target.row === row
    case 'column':
      return target.column === column
    case 'range':
      return inside(areaBetween(target.from, target.to), lineOf(row), column)
    case 'table':
      return true
  }
}

function mapCells(
  section: TableSection,
  change: (cell: TableCell, row: RowRef, column: number) => TableCell,
): TableSection {
  return {
    ...section,
    header: section.header.map((cell, column) => change(cell, 'header', column)),
    rows: section.rows.map((cells, row) => cells.map((cell, column) => change(cell, row, column))),
  }
}

/**
 * The same, for the cells that are drawn only.
 *
 * A cell a merge runs over is not on the page, so a colour or a weight given
 * to it would be invisible until the merge was split, and would then appear
 * from nowhere. It is left exactly as the merge left it.
 */
function mapDrawn(
  section: TableSection,
  change: (cell: TableCell, row: RowRef, column: number) => TableCell,
): TableSection {
  const plan = planOf(section)
  return mapCells(section, (cell, row, column) =>
    startsAt(plan, lineOf(row), column) ? change(cell, row, column) : cell,
  )
}

export function cellAt(section: TableSection, row: RowRef, column: number): TableCell | undefined {
  return cellAtLine(section, lineOf(row), column)
}

/** The drawn cells a target takes in, in reading order. */
export function cellsOf(section: TableSection, target: CellTarget): TableCell[] {
  const plan = planOf(section)
  const found: TableCell[] = []
  const take = (cell: TableCell, row: RowRef, column: number): void => {
    if (covers(target, row, column) && startsAt(plan, lineOf(row), column)) found.push(cell)
  }
  section.header.forEach((cell, column) => take(cell, 'header', column))
  section.rows.forEach((cells, row) => cells.forEach((cell, column) => take(cell, row, column)))
  return found
}

/** Set every drawn cell the target takes in. */
export function setCells(
  section: TableSection,
  target: CellTarget,
  change: CellStyleChange,
): TableSection {
  return mapDrawn(section, (cell, row, column) =>
    covers(target, row, column) ? restyle(cell, change) : cell,
  )
}

/**
 * Empty every drawn cell the target takes in: its text and its settings, both.
 *
 * Whether a column holds figures is kept, because that describes the column
 * and not what was typed into it, and a cleared column that forgot it would
 * set its next figures flush left. A merge is kept too: it is the table's
 * shape, and emptying a heading is not asking for its cells back.
 */
export function clearCells(section: TableSection, target: CellTarget): TableSection {
  return mapDrawn(section, (cell, row, column) =>
    covers(target, row, column)
      ? clean({ value: '', numeric: cell.numeric, colspan: cell.colspan, rowspan: cell.rowspan })
      : cell,
  )
}

/**
 * The settings every targeted cell shares.
 *
 * A property is reported only where all the cells agree on it, so a toolbar
 * over a row of mixed colours shows no colour rather than the first one it
 * found. Showing the first would invite somebody to "leave it as it is" and
 * have it change.
 */
export function commonStyle(section: TableSection, target: CellTarget): CellStyle {
  const cells = cellsOf(section, target)
  const first = cells[0]
  if (!first) return {}

  const agreed = <K extends keyof CellStyle>(key: K): CellStyle[K] | undefined =>
    cells.every((cell) => cell[key] === first[key]) ? first[key] : undefined

  const style: CellStyle = {}
  const align = agreed('align')
  const fill = agreed('fill')
  const ink = agreed('ink')
  if (align) style.align = align
  if (fill) style.fill = fill
  if (ink) style.ink = ink
  if (agreed('strong')) style.strong = true
  if (agreed('ruleAbove')) style.ruleAbove = true
  if (agreed('ruleBelow')) style.ruleBelow = true
  return style
}

/** A fresh row, holding figures wherever the header says the column does. */
function blankRow(section: TableSection): TableCell[] {
  return Array.from({ length: columnCount(section) }, (_, column) =>
    section.header[column]?.numeric ? { value: '', numeric: true } : emptyCell(),
  )
}

function clampIndex(value: number, length: number): number {
  return Math.min(Math.max(0, Math.trunc(value)), length)
}

/** One cell replaced, padding a short line out to reach it. */
function replaceCell(
  section: TableSection,
  line: number,
  column: number,
  cell: TableCell,
): TableSection {
  const rewrite = (cells: readonly TableCell[]): TableCell[] =>
    Array.from({ length: Math.max(cells.length, column + 1) }, (_, at) =>
      at === column ? cell : (cells[at] ?? emptyCell()),
    )
  if (line === 0) return { ...section, header: rewrite(section.header) }
  return {
    ...section,
    rows: section.rows.map((cells, at) => (at === line - 1 ? rewrite(cells) : cells)),
  }
}

/** A merged cell given a new size, written back in full so its settled size is what is stored. */
function respan(
  section: TableSection,
  drawn: DrawnCell,
  size: { readonly colspan?: number; readonly rowspan?: number },
): TableSection {
  const cell = cellAtLine(section, drawn.line, drawn.column) ?? emptyCell()
  return replaceCell(
    section,
    drawn.line,
    drawn.column,
    clean({
      ...cell,
      colspan: size.colspan ?? drawn.colspan,
      rowspan: size.rowspan ?? drawn.rowspan,
    }),
  )
}

/**
 * A row inserted before body row `at`, or at the end when `at` is the count.
 *
 * A merged cell the row lands inside runs over it too, so a row added in the
 * middle of a merged label is part of that label rather than a hole in it.
 */
export function insertRow(section: TableSection, at: number): TableSection {
  const index = clampIndex(at, section.rows.length)
  const across = mergesAcrossLine(planOf(section), index + 1)
  const rows = [...section.rows]
  rows.splice(index, 0, blankRow(section))
  // Each of those starts above the new row, so the insert leaves it where it was.
  return across.reduce<TableSection>(
    (table, drawn) => respan(table, drawn, { rowspan: drawn.rowspan + 1 }),
    { ...section, rows },
  )
}

/**
 * The body row at `at`, removed.
 *
 * A merged cell running over the row is one line shorter for it. One that
 * starts on the row goes on below it: what it holds moves down to the line
 * that takes the removed row's place, so removing the top row of a merged
 * label does not remove the label.
 */
export function removeRow(section: TableSection, at: number): TableSection {
  if (!section.rows[at]) return section
  const line = at + 1
  const plan = planOf(section)

  let table: TableSection = {
    ...section,
    rows: section.rows.filter((_, position) => position !== at),
  }
  for (const drawn of plan.drawn) {
    if (drawn.line < line && drawn.line + drawn.rowspan > line) {
      table = respan(table, drawn, { rowspan: drawn.rowspan - 1 })
    } else if (drawn.line === line && drawn.rowspan > 1) {
      const moved = cellAtLine(section, line, drawn.column) ?? emptyCell()
      table = replaceCell(
        table,
        line,
        drawn.column,
        clean({ ...moved, colspan: drawn.colspan, rowspan: drawn.rowspan - 1 }),
      )
    }
  }
  return table
}

/**
 * Whether body row `from` can be moved to `to`.
 *
 * Not when any row it passes takes part in a merge running down: the move
 * would carry part of a merged cell away from the rest of it, and there is no
 * reading of that which is what somebody meant.
 */
export function canMoveRow(section: TableSection, from: number, to: number): boolean {
  if (from === to || !section.rows[from] || !section.rows[to]) return false
  const plan = planOf(section)
  for (let row = Math.min(from, to); row <= Math.max(from, to); row += 1) {
    if (lineIsTied(plan, row + 1)) return false
  }
  return true
}

/** The body row at `from`, moved to `to`. Anything that cannot move is left alone. */
export function moveRow(section: TableSection, from: number, to: number): TableSection {
  const moved = section.rows[from]
  if (!moved || !canMoveRow(section, from, to)) return section
  const rows = section.rows.filter((_, position) => position !== from)
  rows.splice(to, 0, moved)
  return { ...section, rows }
}

/**
 * A column inserted before column `at`, or at the end when `at` is the count.
 *
 * Inside a merged cell it widens the cell, as a row does.
 */
export function insertColumn(section: TableSection, at: number): TableSection {
  const position = clampIndex(at, columnCount(section))
  const across = mergesAcrossColumn(planOf(section), position)
  const into = (cells: readonly TableCell[]): TableCell[] => {
    const next = [...cells]
    next.splice(position, 0, emptyCell())
    return next
  }
  return across.reduce<TableSection>(
    (table, drawn) => respan(table, drawn, { colspan: drawn.colspan + 1 }),
    {
      ...section,
      header: into(section.header),
      rows: section.rows.map(into),
      columnWidths: widthsAfterInsert(section.columnWidths, columnCount(section), position),
    },
  )
}

/**
 * Never below one column: a table with none cannot be edited back.
 *
 * A merged cell loses a column the way it loses a row, and one that starts on
 * the removed column moves across to the next.
 */
export function removeColumn(section: TableSection, at: number): TableSection {
  const columns = columnCount(section)
  if (columns <= 1 || at < 0 || at >= columns) return section
  const plan = planOf(section)

  const without = (cells: readonly TableCell[]): TableCell[] =>
    cells.filter((_, position) => position !== at)
  let table: TableSection = {
    ...section,
    header: without(section.header),
    rows: section.rows.map(without),
    columnWidths: widthsAfterRemove(section.columnWidths, columns, at),
  }
  for (const drawn of plan.drawn) {
    if (drawn.column < at && drawn.column + drawn.colspan > at) {
      table = respan(table, drawn, { colspan: drawn.colspan - 1 })
    } else if (drawn.column === at && drawn.colspan > 1) {
      const moved = cellAtLine(section, drawn.line, at) ?? emptyCell()
      table = replaceCell(
        table,
        drawn.line,
        at,
        clean({ ...moved, colspan: drawn.colspan - 1, rowspan: drawn.rowspan }),
      )
    }
  }
  return table
}

/** Whether column `from` can be moved to `to`, under the same rule as a row. */
export function canMoveColumn(section: TableSection, from: number, to: number): boolean {
  const columns = columnCount(section)
  if (from === to || from < 0 || to < 0 || from >= columns || to >= columns) return false
  const plan = planOf(section)
  for (let column = Math.min(from, to); column <= Math.max(from, to); column += 1) {
    if (columnIsTied(plan, column)) return false
  }
  return true
}

/** The column at `from`, moved to `to`. Anything that cannot move is left alone. */
export function moveColumn(section: TableSection, from: number, to: number): TableSection {
  if (!canMoveColumn(section, from, to)) return section
  const shifted = (cells: readonly TableCell[]): TableCell[] => {
    const next = [...cells]
    const [moved] = next.splice(from, 1)
    if (moved) next.splice(to, 0, moved)
    return next
  }
  return {
    ...section,
    header: shifted(section.header),
    rows: section.rows.map(shifted),
    columnWidths: widthsAfterMove(section.columnWidths, columnCount(section), from, to),
  }
}

/**
 * Whether a column holds figures.
 *
 * Read off the header first, because the header is what an import and the
 * toolbar both set; and off the body when the header does not say, because
 * a document written by hand may never have marked its header at all. Cells
 * a merge runs over say nothing either way and are not asked.
 */
export function columnIsNumeric(section: TableSection, column: number): boolean {
  if (section.header[column]?.numeric) return true
  const plan = planOf(section)
  const drawn = section.rows.filter((_, row) => startsAt(plan, row + 1, column))
  return drawn.length > 0 && drawn.every((row) => row[column]?.numeric === true)
}

/**
 * Mark a whole column as figures, or as words.
 *
 * A column at a time rather than a cell at a time, because that is the unit
 * that is actually wrong: an import reads one column as text and every cell in
 * it needs the same correction. Cells a merge runs over are marked too, so
 * they are right the day the merge is split.
 */
export function setColumnNumeric(
  section: TableSection,
  column: number,
  numeric: boolean,
): TableSection {
  return mapCells(section, (cell, _row, position) =>
    position === column ? clean({ ...cell, numeric }) : cell,
  )
}

// ── Merging ────────────────────────────────────────────────────────────────

/**
 * The block of the table a target stands for, grown over any merged cell it
 * cuts. Null when the target names nothing in the table.
 */
export function areaOf(section: TableSection, target: CellTarget): CellArea | null {
  const plan = planOf(section)
  switch (target.scope) {
    case 'cell': {
      const drawn = plan.at(lineOf(target.row), target.column)
      return drawn ? areaOfDrawn(drawn) : null
    }
    case 'row': {
      const line = lineOf(target.row)
      return line < plan.lines
        ? settleArea(plan, { top: line, left: 0, bottom: line, right: plan.columns - 1 })
        : null
    }
    case 'column':
      return target.column < plan.columns
        ? settleArea(plan, {
            top: 0,
            left: target.column,
            bottom: plan.lines - 1,
            right: target.column,
          })
        : null
    case 'range':
      return settleArea(plan, areaBetween(target.from, target.to))
    case 'table':
      return { top: 0, left: 0, bottom: plan.lines - 1, right: plan.columns - 1 }
  }
}

/**
 * A target as it stands on the table now, or null when it no longer names
 * anything there.
 *
 * A position a merge runs over is taken as the merged cell, and a range is
 * grown over the merged cells it cuts; a range that comes to exactly one cell
 * is that cell. Worked out once, when the target is read, so what is shown
 * selected is what every operation then acts on.
 */
export function settleTarget(section: TableSection, target: CellTarget): CellTarget | null {
  const plan = planOf(section)
  switch (target.scope) {
    case 'cell': {
      const drawn = plan.at(lineOf(target.row), target.column)
      if (!drawn) return null
      return drawn.line === lineOf(target.row) && drawn.column === target.column
        ? target
        : { scope: 'cell', row: rowOf(drawn.line), column: drawn.column }
    }
    case 'row':
      return lineOf(target.row) < plan.lines ? target : null
    case 'column':
      return target.column < plan.columns ? target : null
    case 'table':
      return target
    case 'range': {
      const area = settleArea(plan, areaBetween(target.from, target.to))
      if (!area) return null
      const [only, ...more] = drawnIn(plan, area)
      if (only && more.length === 0) {
        return { scope: 'cell', row: rowOf(only.line), column: only.column }
      }
      return {
        scope: 'range',
        from: { row: rowOf(area.top), column: area.left },
        to: { row: rowOf(area.bottom), column: area.right },
      }
    }
  }
}

/**
 * Whether a target can be merged into one cell.
 *
 * When it takes in more than one drawn cell, and is not the whole table: a
 * table merged into a single cell is not a table, and the one button that
 * would do it is a button that loses every figure but one.
 */
export function canMerge(section: TableSection, target: CellTarget): boolean {
  if (target.scope === 'table') return false
  const area = areaOf(section, target)
  return area !== null && drawnIn(planOf(section), area).length > 1
}

/** Whether a target takes in a merged cell that could be split. */
export function canSplit(section: TableSection, target: CellTarget): boolean {
  const area = areaOf(section, target)
  return area !== null && drawnIn(planOf(section), area).some(isMerged)
}

/**
 * How many texts a merge would lose.
 *
 * A merged cell holds one text, so every text but the first is dropped. Said
 * before the merge is made rather than discovered after it, so nobody merges
 * two figures thinking they will be added up or kept side by side.
 */
export function textsLostByMerge(section: TableSection, target: CellTarget): number {
  const area = areaOf(section, target)
  if (!area) return 0
  const written = drawnIn(planOf(section), area).filter(
    (drawn) => (cellAtLine(section, drawn.line, drawn.column)?.value.trim() ?? '') !== '',
  )
  return Math.max(0, written.length - 1)
}

/** Every row padded out to the table's width, so each position has a cell to write. */
function squared(section: TableSection): TableSection {
  const columns = columnCount(section)
  const pad = (cells: readonly TableCell[]): TableCell[] =>
    Array.from({ length: columns }, (_, at) => cells[at] ?? emptyCell())
  return { ...section, header: pad(section.header), rows: section.rows.map(pad) }
}

/**
 * The target made into one cell.
 *
 * The merged cell takes the first text in reading order, with that cell's
 * settings, so merging an empty heading cell with the "2026" beside it gives
 * "2026" rather than nothing. Every other cell in the block is emptied, keeping
 * only whether its column holds figures.
 *
 * A header cell merged across columns is centred unless somebody already
 * aligned it: it is a heading standing over the columns it names, and those
 * are centred over them. Nowhere else. In the body a merge across columns is a
 * label, "Total" over the label columns or a line naming the rows below it,
 * and a label is read from the left. The alignment buttons change either in
 * one click.
 */
export function mergeCells(section: TableSection, target: CellTarget): TableSection {
  if (!canMerge(section, target)) return section
  const table = squared(section)
  const area = areaOf(table, target)
  if (!area) return section

  const cells = drawnIn(planOf(table), area).map(
    (drawn) => cellAtLine(table, drawn.line, drawn.column) ?? emptyCell(),
  )
  const kept = cells.find((cell) => cell.value.trim() !== '') ?? cells[0] ?? emptyCell()
  const colspan = area.right - area.left + 1
  const rowspan = area.bottom - area.top + 1
  const merged = clean({
    ...kept,
    colspan,
    rowspan,
    align: kept.align ?? (colspan > 1 && area.top === 0 ? 'center' : undefined),
  })

  return mapCells(table, (cell, row, column) => {
    const line = lineOf(row)
    if (!inside(area, line, column)) return cell
    if (line === area.top && column === area.left) return merged
    return clean({ value: '', numeric: cell.numeric })
  })
}

/**
 * Every merged cell the target takes in, made back into its cells.
 *
 * The text and settings stay in the top-left cell, where the merge kept them,
 * and the others come back empty. Nothing is invented for them: the texts a
 * merge dropped are gone, and undo is the way back to them.
 */
export function splitCells(section: TableSection, target: CellTarget): TableSection {
  const area = areaOf(section, target)
  if (!area) return section
  return drawnIn(planOf(section), area)
    .filter(isMerged)
    .reduce<TableSection>((table, drawn) => {
      const cell = cellAtLine(table, drawn.line, drawn.column) ?? emptyCell()
      return replaceCell(
        table,
        drawn.line,
        drawn.column,
        clean({ ...cell, colspan: undefined, rowspan: undefined }),
      )
    }, section)
}
